import React, {useEffect, useState} from "react";
import {useHistory} from "react-router-dom";
import {Alert, Button, Modal, Space, Table, Tag, message} from "antd";
import {DeleteOutlined, EditOutlined, EyeOutlined, LockOutlined, PlusOutlined} from "@ant-design/icons";
import {ApiRolesService} from "../../../../../shared/api";
import {usersModel} from "../../../../../entities/users";
import {rolesModel} from "../../../../../entities/roles";
import {routeNames} from "../../../../../shared/router/routeNames";
import {useCanEdit} from "../../../sectionAccess";
import {SectionToolbar, countText, useSectionSearch} from "../../components/SectionToolbar/SectionToolbar";
import {RoleEditor} from "./RoleEditor";

const errorOf = (error) => error?.response?.data?.errors || {}

// what a role gives, in a line: its sections, portal, fields
const summary = (role, catalog) => {
  if (role.locked) return [<Tag key='all' color='gold'>все права</Tag>]
  const p = role.permissions || {}
  const titles = Object.fromEntries((catalog ? catalog.sections : []).map((s) => [s.key, s.title]))
  const tags = Object.entries(p.sections || {}).map(([key, level]) => (
    <Tag key={key} color={level === 'edit' ? 'blue' : 'default'}>{titles[key] || key}{level === 'view' ? ' · просмотр' : ''}</Tag>
  ))
  if (p.portal) tags.push(<Tag key='portal' color='green'>портал</Tag>)
  const flats = p.flats || {}
  if (Array.isArray(flats.categories)) tags.push(<Tag key='categories' color='purple'>категорий: {flats.categories.length}</Tag>)
  const rules = ((catalog && catalog.flatRules) || []).filter((rule) => flats[rule.key]).map((rule) => rule.title.toLowerCase())
  if (rules.length) tags.push(<Tag key='flats' color='purple'>видит: {rules.join(', ')}</Tag>)
  const visible = p.fields && p.fields.visible
  tags.push(<Tag key='fields'>{visible === null ? 'все поля' : `полей: ${(visible || []).length}`}</Tag>)
  return tags
}

// «Роли»: what each role may do; a user may have several roles (section «Пользователи»)
export const RolesSection = () => {
  const canEdit = useCanEdit()
  const history = useHistory()
  const [roles, setRoles] = useState([])
  const [catalog, setCatalog] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [editing, setEditing] = useState(null) // {role} | {role: null} for a new one
  const [search, setSearch, shown] = useSectionSearch(roles, ['title', 'description'])

  const load = () => {
    setLoading(true)
    Promise.all([ApiRolesService.fetchRoles(), catalog ? null : ApiRolesService.fetchCatalog()])
      .then(([list, cat]) => {
        setRoles(list.data)
        if (cat) setCatalog(cat.data)
        setError(null)
        // the users section shows role names from this model
        rolesModel.setRoles(list.data)
      })
      .catch(() => setError('Не удалось загрузить роли. Обновите страницу.'))
      .finally(() => setLoading(false))
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(load, [])

  const showUsers = (role) => {
    usersModel.setFilters({role: role.id, search: '', status: undefined})
    history.push(`${routeNames.ADMIN}/users`)
  }

  const remove = (role) => {
    if (role.users > 0) {
      Modal.warning({
        title: `Роль «${role.title}» есть у ${role.users} ${role.users === 1 ? 'пользователя' : 'пользователей'}`,
        content: 'Сначала уберите эту роль у всех пользователей (раздел «Пользователи»), потом её можно удалить.',
        okText: 'Показать пользователей',
        onOk: () => showUsers(role),
        closable: true,
      })
      return
    }
    Modal.confirm({
      title: `Удалить роль «${role.title}»?`,
      content: 'Пользователей с этой ролью нет. Удаление не отменить.',
      okText: 'Удалить',
      okButtonProps: {danger: true},
      cancelText: 'Отмена',
      onOk: async () => {
        try {
          await ApiRolesService.remove(role.id)
          message.success('Роль удалена')
          load()
        } catch (e) {
          message.error(errorOf(e).message || 'Не удалось удалить')
        }
      },
    })
  }

  const columns = [
    {
      title: 'Роль',
      key: 'title',
      render: (role) => (
        <>
          <b>{role.title || role.name}</b>
          {role.locked && <LockOutlined style={{marginLeft: 6, color: '#999'}} title='Не меняется'/>}
          {role.description && <div className='cell-hint'>{role.description}</div>}
        </>
      ),
    },
    {
      title: 'Права',
      key: 'rights',
      render: (role) => <div className='tag-list'>{summary(role, catalog)}</div>,
    },
    {
      title: 'Пользователей',
      key: 'users',
      align: 'right',
      width: 130,
      render: (role) => (role.users
        ? <Button type='link' size='small' onClick={() => showUsers(role)}>{role.users}</Button>
        : <span className='cell-empty'>0</span>),
    },
    {
      title: '',
      key: 'actions',
      width: 110,
      render: (role) => (
        <Space>
          <Button
            icon={canEdit && !role.locked ? <EditOutlined/> : <EyeOutlined/>}
            title={canEdit && !role.locked ? 'Изменить' : 'Посмотреть'}
            onClick={() => setEditing({role})}
            disabled={!catalog}
          />
          {canEdit && !role.undeletable && <Button danger icon={<DeleteOutlined/>} title='Удалить' onClick={() => remove(role)}/>}
        </Space>
      ),
    },
  ]

  return (
    <>
      <Alert
        className='section-note'
        type='info'
        showIcon
        message='Роль — набор прав: разделы админки (просмотр или правка), портал агента, какие квартиры видит (категории, скрытые, брони, Запас…) и служебные фильтры, поля квартир. У пользователя может быть несколько ролей — права складываются. Роли пользователю даются в разделе «Пользователи».'
      />
      {error && <Alert className='section-note' type='error' message={error}/>}
      <Table
        rowKey='id'
        title={() => (
          <SectionToolbar search={search} onSearch={setSearch} placeholder='Название или описание' count={countText(roles.length, shown.length, search)}>
            {canEdit && <Button type='primary' icon={<PlusOutlined/>} disabled={!catalog} onClick={() => setEditing({role: null})}>Добавить роль</Button>}
          </SectionToolbar>
        )}
        columns={columns}
        dataSource={shown}
        loading={loading}
        pagination={false}
        locale={{emptyText: 'Ничего не нашлось'}}
      />
      {editing && catalog && (
        <RoleEditor
          role={editing.role}
          roles={roles}
          catalog={catalog}
          readOnly={!canEdit}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null)
            load()
          }}
        />
      )}
    </>
  )
}
